import React, { forwardRef } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement
} from 'chart.js';

import Overview from "../tabs/Overview";
import Removals from "../tabs/Removals";
import Sectors from "../tabs/Sectors";
import Impact from "../tabs/Impact";
import Power from "../tabs/Power";
import { calculateTotalImpact } from '../../utils/calculationHelpers';
import { getScenarioConfig } from '../../config/scenarioConfigs';
import PowerGenerationSystemSize from './PowerGenerationSystemSize';
import PowerGenerationLandUsage from './PowerGenerationLandUsage';
import AbatementPathwayGraph from './AbatementPathwayGraph';
import { DASHBOARD_COLORS, getSectorColor, getEnergyColor, getNetZeroColor, getAbatementColor } from '../../config/colorScheme';
import { OverallEmissionsProjection } from "./OverallEmissionsProjection";
import { CapitalExpenditureChart } from "./CapitalExpenditureChart";
import { CapitalExpenditureBySectorChart } from "./CapitalExpenditureBySectorChart";


// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement
);
ChartJS.defaults.plugins.legend.onClick = () => {};
ChartJS.defaults.events = []; // no hover, no clicks anywhere
ChartJS.defaults.hover.mode = null;           // no hover mode
ChartJS.defaults.elements.point.hoverRadius = 0;

// Constants for scenario keys
const PANEL_SCENARIO_KEY = 'net_zero_futures_dvl_shift_high_transstshiftec_2001';
const PANEL_SECTOR_CONFIG = { food: 'panel', buildings: 'panel', transport: 'panel', industry: 'panel' };

const ResultsTabs = forwardRef(({
  activeTab,
  setActiveTab,
  sectorData,
  sectorImpact,
  ambitionLevel
}, ref) => {
  // State for comparison scenarios
  const [comparisonScenario, setComparisonScenario] = React.useState(null);
  const [electricityComparisonScenario, setElectricityComparisonScenario] = React.useState(null);
  const [carbonIntensityComparisonScenario, setCarbonIntensityComparisonScenario] = React.useState(null);
  const [carbonRemovalComparisonScenario, setCarbonRemovalComparisonScenario] = React.useState(null);
  const [electricityYearsComparisonScenario, setElectricityYearsComparisonScenario] = React.useState({ scenario: 'transform', variant: 'high' });
  // Electricity Generation Context Calculation
  const calculatePowerGenerationContext = (sectorImpact, totalCapacityMW) => {
    // Current UK electricity generation and import context (2023-2024)
    const currentAnnualGeneration = 292.7; // TWh in 2023
    const currentAnnualImports = 26.3; // TWh from January to September 2024
    const totalCurrentElectricity = currentAnnualGeneration + currentAnnualImports;

    // UK Household Context
    const ukHouseholds = 28400000; // 28.4 million households

    // Sector impact multipliers for comparisons
    const sectorMultipliers = {
      food: {
        current: 1.0,
        low: 0.9,
        high: 0.8
      },
      buildings: {
        current: 1.0,
        low: 0.9,
        high: 0.7
      },
      transport: {
        current: 1.0,
        low: 0.9,
        high: 0.6
      },
      industry: {
        current: 1.0,
        low: 0.9,
        high: 0.7
      }
    };

    // Calculate cumulative reduction factor
    const cumulativeReductionFactor = Object.keys(sectorMultipliers).reduce((acc, sector) => {
      return acc * sectorMultipliers[sector][sectorImpact[sector]];
    }, 1);

    // Calculate adjusted electricity demand
    const adjustedElectricity = Math.round(totalCurrentElectricity * cumulativeReductionFactor);

    // Detailed calculations for comparisons
    const evChargesPerHousehold = Math.round(
      319_600_000_000 // Total UK electricity in kWh
      / 50 // kWh per full EV charge
      / ukHouseholds // Divide by number of households
    );

    const fridgesPerHousehold = Math.round(
      319_600_000_000 // Total UK electricity in kWh
      / (2 * 365) // kWh per fridge per day * 365 days = annual energy per fridge
      / ukHouseholds // Divide by total UK households
    );

    // Manchester to Liverpool rail trips calculation
    const manchesterLiverpoolTripsPerHousehold = Math.round(
      319_600_000_000 // Total UK electricity in kWh
      / (15.71 * 31) // kWh per rail trip (31 miles between Manchester and Liverpool)
      / ukHouseholds // Divide by number of households
    );

    // Tangible comparisons based on sector choices
    const comparisons = [
      {
        name: 'Refrigerator Operation',
        unit: 'Fridges per Household',
        value: {
          fridgesPerHousehold: fridgesPerHousehold,
          years: 1
        },
        description: 'Number of fridges that could theoretically run if ALL UK electricity was used solely for refrigeration',
        impactFactors: {
          food: 1,
          buildings: 1
        }
      }
    ];

    // Log detailed calculation information
    // console.log('Electricity Generation Context Calculation:', {
    //   currentAnnualGeneration,
    //   currentAnnualImports,
    //   totalCurrentElectricity,
    //   ukHouseholds,
    //   cumulativeReductionFactor,
    //   adjustedElectricity,
    //   evChargesPerHousehold,
    //   fridgesPerHousehold,
    //   manchesterLiverpoolTripsPerHousehold,
    //   sectorImpact,
    //   comparisons
    // });

    // Calculate baseline values (what the current scenario would show)
    const calculateBaselineValue = (comparisonName) => {
      // Use baseline reduction factor (all sectors at "current")
      let baselineReductionFactor = 1;

      if (comparisonName === 'Refrigerator Operation') {
        // Affected by food & buildings
        baselineReductionFactor *= sectorMultipliers.food.current;
        baselineReductionFactor *= sectorMultipliers.buildings.current;
        return Math.round(fridgesPerHousehold * baselineReductionFactor);
      }

      return 0; // fallback
    };

    // Adjust values based on sector impact
    const adjustedComparisons = comparisons.map(comparison => {
      // Calculate sector-specific reduction factor based on the comparison's impact factors
      let sectorSpecificReductionFactor = 1;

      // Apply sector multipliers based on which sectors affect this comparison
      Object.keys(comparison.impactFactors).forEach(sector => {
        if (sectorMultipliers[sector] && sectorImpact[sector]) {
          sectorSpecificReductionFactor *= sectorMultipliers[sector][sectorImpact[sector]];
        }
      });

      // Calculate baseline value for this comparison
      const baselineValue = calculateBaselineValue(comparison.name);

      // Special handling for Refrigerator Operation
      if (comparison.name === 'Refrigerator Operation') {
        // Ensure comparison.value is an object with expected properties
        const fridgeValue = typeof comparison.value === 'object'
          ? comparison.value
          : { fridgesPerHousehold: comparison.value, years: 1 };

        return {
          ...comparison,
          value: {
            fridgesPerHousehold: Math.round(
              fridgeValue.fridgesPerHousehold * sectorSpecificReductionFactor
            ),
            years: fridgeValue.years || 1
          },
          baselineValue: baselineValue
        };
      }

      // Default handling for other comparisons
      return {
        ...comparison,
        value: Math.round(
          (typeof comparison.value === 'object'
            ? comparison.value.fridgesPerHousehold || comparison.value
            : comparison.value) * sectorSpecificReductionFactor
        ),
        baselineValue: baselineValue
      };
    });

    // Force some minimum values if calculations result in zero
    const finalComparisons = adjustedComparisons.map(comparison => {
      if (comparison.name === 'Refrigerator Operation' && comparison.value.fridgesPerHousehold === 0) {
        return {
          ...comparison,
          value: {
            fridgesPerHousehold: 5,
            years: 1
          }
        };
      }
      return comparison;
    });


    // Electricity Demand Projection (based on UK projections of 570-770 TWh by 2050)
    const baselineProjection = [
      { year: 2025, demand: 350 },  // Constant starting point
      { year: 2030, demand: 430 },  // Increasing demand
      { year: 2035, demand: 520 },  // Continued growth
      { year: 2040, demand: 620 },  // Significant increase
      { year: 2045, demand: 700 },  // Near peak
      { year: 2050, demand: 770 }   // Maximum projected demand
    ];

    // Adjust projection based on sector impact
    const adjustedProjection = baselineProjection.map((point, index) => ({
      ...point,
      demand: index === 0
        ? point.demand  // Keep 2025 constant
        : Math.round(point.demand * cumulativeReductionFactor)
    }));

    return {
      currentAnnualGeneration,
      currentAnnualImports,
      totalCurrentElectricity,
      generationChange: {
        absolute: adjustedElectricity - totalCurrentElectricity,
        percentage: Math.round(((adjustedElectricity - totalCurrentElectricity) / totalCurrentElectricity) * 100)
      },
      comparisons: finalComparisons,
      cumulativeReductionFactor,
      electricityDemandProjection: {
        baseline: baselineProjection,
        adjusted: adjustedProjection
      }
    };
  };

  // Investment calculation function
  const calculateInvestment = () => {
    // Base investment figures from Climate Change Committee and government estimates
    const baseAnnualInvestment = 2064; // Current annual household investment

    // Scenario-specific investment multipliers
    const scenarioMultipliers = {
      current: 1, // Base scenario
      steer: 1.5, // Moderate increase in investment
      shift: 2, // Significant increase in investment
      transform: 3 // Transformative investment scenario
    };

    // Determine the current scenario based on sector impact
    const getCurrentScenario = () => {
      // You can customize this logic based on how you want to determine the scenario
      // For now, let's use a simple heuristic based on the number of high-impact sectors
      const highImpactSectors = Object.values(sectorImpact).filter(impact => impact === 'high').length;

      if (highImpactSectors === 0) return 'current';
      if (highImpactSectors <= 1) return 'steer';
      if (highImpactSectors <= 2) return 'shift';
      return 'transform';
    };

    const currentScenario = getCurrentScenario();

    // Calculate total power generation capacity
    const totalCapacity = powerGenerationNeeds.totalCapacityMW || 3000;

    // Investment calculations based on government estimates
    const baseAnnualEconomyInvestment = 40000000000; // £40 billion per year for clean energy
    const totalUKPopulation = 67000000; // Approximate UK population
    const totalUKHouseholds = 29000000;
    const scenarioMultiplier = scenarioMultipliers[currentScenario];

    // Adjust investment based on total capacity and scenario
    const capacityInvestmentFactor = Math.max(1, totalCapacity / 3000); // Base capacity is 3000 MW
    const adjustedScenarioMultiplier = scenarioMultiplier * capacityInvestmentFactor;

    // Annual investment per household calculation
    const annualInvestmentPerHousehold = Math.round(
      (baseAnnualEconomyInvestment / totalUKHouseholds) * adjustedScenarioMultiplier
    );

    // Per person per week calculation (based on total population)
    const annualInvestmentPerPerson = Math.round(
      (baseAnnualEconomyInvestment / totalUKPopulation) * adjustedScenarioMultiplier
    );
    const weeklyInvestmentPerPerson = Math.round(annualInvestmentPerPerson / 52);

    // Total investment over 25 years
    const totalInvestmentPerHousehold = annualInvestmentPerHousehold * 25;

    // Calculate investment increase percentage
    const investmentIncrease = Math.round((annualInvestmentPerHousehold / baseAnnualInvestment - 1) * 100);

    return {
      scenario: currentScenario,
      totalInvestment: totalInvestmentPerHousehold * totalUKHouseholds,
      investmentPerHousehold: totalInvestmentPerHousehold,
      annualInvestmentPerHousehold,
      baseAnnualInvestment,
      investmentIncrease,
      capacityInvestmentFactor,
      weeklyInvestmentPerPerson,
      annualInvestmentPerPerson
    };
  };

  // Dynamic Energy Demand Projection Function
  const calculateEnergyDemandProjection = (sectorImpact) => {
    // Base projection starting point
    const baselineProjection = 1500;

    // Impact multipliers for each sector
    const sectorMultipliers = {
      food: {
        current: 1.0,
        low: 0.9,
        high: 0.8
      },
      buildings: {
        current: 1.0,
        low: 0.9,
        high: 0.85
      },
      transport: {
        current: 1.0,
        low: 0.9,
        high: 0.8
      },
      industry: {
        current: 1.0,
        low: 0.9,
        high: 0.85
      }
    };

    // Calculate cumulative reduction factor
    const cumulativeReductionFactor = Object.keys(sectorMultipliers).reduce((acc, sector) => {
      return acc * sectorMultipliers[sector][sectorImpact[sector]];
    }, 1);

    // Generate projection data points (2025-2050)
    const projectionData = [
      baselineProjection * (1 - (1 - cumulativeReductionFactor) * 0.2),
      baselineProjection * (1 - (1 - cumulativeReductionFactor) * 0.4),
      baselineProjection * (1 - (1 - cumulativeReductionFactor) * 0.6),
      baselineProjection * (1 - (1 - cumulativeReductionFactor) * 0.8),
      baselineProjection * (1 - (1 - cumulativeReductionFactor) * 0.9),
      baselineProjection * cumulativeReductionFactor
    ];

    // Calculate Net Zero Target (dynamically based on sector impacts)
    const netZeroTarget = baselineProjection * Math.max(0.6, cumulativeReductionFactor);

    return {
      projectionData,
      netZeroTarget
    };
  };

  // Function to calculate comparison scenario data
  const calculateComparisonEnergyDemandProjection = (scenarioKey, variant = 'low') => {
    // Import scenario configurations
    const scenarioConfigs = {
      current: {
        low: {
          food: 'current',
          buildings: 'current',
          transport: 'current',
          industry: 'current'
        },
        high: {
          food: 'current',
          buildings: 'current',
          transport: 'current',
          industry: 'current'
        }
      },
      steer: {
        low: {
          food: 'current',
          buildings: 'current',
          transport: 'low',
          industry: 'low'
        },
        high: {
          food: 'current',
          buildings: 'low',
          transport: 'low',
          industry: 'low'
        }
      },
      shift: {
        low: {
          food: 'low',
          buildings: 'low',
          transport: 'high',
          industry: 'low'
        },
        high: {
          food: 'low',
          buildings: 'high',
          transport: 'low',
          industry: 'low'
        }
      },
      transform: {
        low: {
          food: 'low',
          buildings: 'high',
          transport: 'high',
          industry: 'low'
        },
        high: {
          food: 'high',
          buildings: 'high',
          transport: 'high',
          industry: 'high'
        }
      }
    };

    const comparisonSectorImpact = scenarioConfigs[scenarioKey]?.[variant] || scenarioConfigs.current.low;
    return calculateEnergyDemandProjection(comparisonSectorImpact);
  };

  // Function to calculate comparison power generation context
  const calculateComparisonPowerGenerationContext = (scenarioKey, variant = 'low') => {
    // Use the same scenario configurations
    const scenarioConfigs = {
      current: {
        low: {
          food: 'current',
          buildings: 'current',
          transport: 'current',
          industry: 'current'
        },
        high: {
          food: 'current',
          buildings: 'current',
          transport: 'current',
          industry: 'current'
        }
      },
      steer: {
        low: {
          food: 'current',
          buildings: 'current',
          transport: 'low',
          industry: 'low'
        },
        high: {
          food: 'current',
          buildings: 'low',
          transport: 'low',
          industry: 'low'
        }
      },
      shift: {
        low: {
          food: 'low',
          buildings: 'low',
          transport: 'high',
          industry: 'low'
        },
        high: {
          food: 'low',
          buildings: 'high',
          transport: 'low',
          industry: 'low'
        }
      },
      transform: {
        low: {
          food: 'low',
          buildings: 'high',
          transport: 'high',
          industry: 'low'
        },
        high: {
          food: 'high',
          buildings: 'high',
          transport: 'high',
          industry: 'high'
        }
      }
    };

    const comparisonSectorImpact = scenarioConfigs[scenarioKey]?.[variant] || scenarioConfigs.current.low;
    const comparisonPowerNeeds = calculatePowerGenerationNeeds(comparisonSectorImpact);
    return calculatePowerGenerationContext(comparisonSectorImpact, comparisonPowerNeeds.totalCapacityMW);
  };

  // Function to calculate comparison carbon intensity data
  const calculateComparisonCarbonIntensity = (scenarioKey, variant = 'low') => {
    // Use the same scenario configurations
    const scenarioConfigs = {
      current: {
        low: {
          food: 'current',
          buildings: 'current',
          transport: 'current',
          industry: 'current'
        },
        high: {
          food: 'current',
          buildings: 'current',
          transport: 'current',
          industry: 'current'
        }
      },
      steer: {
        low: {
          food: 'current',
          buildings: 'current',
          transport: 'low',
          industry: 'low'
        },
        high: {
          food: 'current',
          buildings: 'low',
          transport: 'low',
          industry: 'low'
        }
      },
      shift: {
        low: {
          food: 'low',
          buildings: 'low',
          transport: 'high',
          industry: 'low'
        },
        high: {
          food: 'low',
          buildings: 'high',
          transport: 'low',
          industry: 'low'
        }
      },
      transform: {
        low: {
          food: 'low',
          buildings: 'high',
          transport: 'high',
          industry: 'low'
        },
        high: {
          food: 'high',
          buildings: 'high',
          transport: 'high',
          industry: 'high'
        }
      }
    };

    const comparisonSectorImpact = scenarioConfigs[scenarioKey]?.[variant] || scenarioConfigs.current.low;
    return calculateCarbonIntensity(comparisonSectorImpact);
  };

  // Function to calculate comparison carbon removal technologies data
  const calculateComparisonCarbonRemovalTechnologies = (scenarioKey, variant = 'low') => {
    // Use the same scenario configurations
    const scenarioConfigs = {
      current: {
        low: {
          food: 'current',
          buildings: 'current',
          transport: 'current',
          industry: 'current'
        },
        high: {
          food: 'current',
          buildings: 'current',
          transport: 'current',
          industry: 'current'
        }
      },
      steer: {
        low: {
          food: 'current',
          buildings: 'current',
          transport: 'low',
          industry: 'low'
        },
        high: {
          food: 'current',
          buildings: 'low',
          transport: 'low',
          industry: 'low'
        }
      },
      shift: {
        low: {
          food: 'low',
          buildings: 'low',
          transport: 'high',
          industry: 'low'
        },
        high: {
          food: 'low',
          buildings: 'high',
          transport: 'low',
          industry: 'low'
        }
      },
      transform: {
        low: {
          food: 'low',
          buildings: 'high',
          transport: 'high',
          industry: 'low'
        },
        high: {
          food: 'high',
          buildings: 'high',
          transport: 'high',
          industry: 'high'
        }
      }
    };

    const comparisonSectorImpact = scenarioConfigs[scenarioKey]?.[variant] || scenarioConfigs.current.low;
    return calculateCarbonRemovalTechnologiesProjection(comparisonSectorImpact);
  };

  // Dynamic Emissions Reduction Projection Function
  const calculateEmissionsReductionProjection = (sectorImpact) => {
    // Base projection starting point (in MtCO₂/year)
    const baselineEmissions = 450; // UK's 2020 emissions level

    // Emissions reduction multipliers for each sector
    const sectorReductionMultipliers = {
      food: {
        current: 1.0,
        low: 0.85,
        high: 0.7
      },
      buildings: {
        current: 1.0,
        low: 0.75,
        high: 0.6
      },
      transport: {
        current: 1.0,
        low: 0.75,
        high: 0.5
      },
      industry: {
        current: 1.0,
        low: 0.8,
        high: 0.65
      }
    };

    // Calculate cumulative reduction factor
    const cumulativeReductionFactor = Object.keys(sectorReductionMultipliers).reduce((acc, sector) => {
      return acc * sectorReductionMultipliers[sector][sectorImpact[sector]];
    }, 1);

    // Generate emissions projection data points
    const emissionsProjectionData = [
      baselineEmissions,
      baselineEmissions * (1 - (1 - cumulativeReductionFactor) * 0.2),
      baselineEmissions * (1 - (1 - cumulativeReductionFactor) * 0.4),
      baselineEmissions * (1 - (1 - cumulativeReductionFactor) * 0.6),
      baselineEmissions * (1 - (1 - cumulativeReductionFactor) * 0.8),
      baselineEmissions * cumulativeReductionFactor,
      baselineEmissions * cumulativeReductionFactor
    ];

    // Calculate Net Zero Emissions Target
    const netZeroEmissionsTarget = baselineEmissions * Math.max(0.1, cumulativeReductionFactor);

    return {
      emissionsProjectionData,
      netZeroEmissionsTarget
    };
  };

  // Dynamic Emissions Reduction Calculation
  const calculateEmissionsReductionPercentage = (sectorImpact) => {
    // Base projection starting point (in MtCO₂/year)
    const baselineEmissions = 450; // UK's 2020 emissions level

    // Emissions reduction multipliers for each sector
    const sectorReductionMultipliers = {
      food: {
        current: 1.0,
        low: 0.85,
        high: 0.7
      },
      buildings: {
        current: 1.0,
        low: 0.75,
        high: 0.6
      },
      transport: {
        current: 1.0,
        low: 0.75,
        high: 0.5
      },
      industry: {
        current: 1.0,
        low: 0.8,
        high: 0.65
      }
    };

    // Calculate cumulative reduction factor
    const cumulativeReductionFactor = Object.keys(sectorReductionMultipliers).reduce((acc, sector) => {
      return acc * sectorReductionMultipliers[sector][sectorImpact[sector]];
    }, 1);

    // Calculate final emissions in 2050
    const finalEmissions = baselineEmissions * cumulativeReductionFactor;

    // Calculate percentage reduction
    const reductionPercentage = Math.round((1 - finalEmissions / baselineEmissions) * 100);

    return reductionPercentage;
  };

  // Dynamic Energy Demand Reduction Calculation
  const calculateEnergyDemandReductionPercentage = (sectorImpact) => {
    // Base projection starting point (in TWh)
    const baselineEnergyDemand = 1500; // Baseline energy demand in 1990

    // Energy demand reduction multipliers for each sector
    const sectorReductionMultipliers = {
      food: {
        current: 1.0,
        low: 0.9,
        high: 0.8
      },
      buildings: {
        current: 1.0,
        low: 0.9,
        high: 0.85
      },
      transport: {
        current: 1.0,
        low: 0.9,
        high: 0.8
      },
      industry: {
        current: 1.0,
        low: 0.9,
        high: 0.85
      }
    };

    // Calculate cumulative reduction factor
    const cumulativeReductionFactor = Object.keys(sectorReductionMultipliers).reduce((acc, sector) => {
      return acc * sectorReductionMultipliers[sector][sectorImpact[sector]];
    }, 1);

    // Calculate final energy demand in 2050
    const finalEnergyDemand = baselineEnergyDemand * cumulativeReductionFactor;

    // Calculate percentage reduction
    const reductionPercentage = Math.round((1 - finalEnergyDemand / baselineEnergyDemand) * 100);

    return reductionPercentage;
  };

  // Energy Demand Graph Configuration
  const { projectionData, netZeroTarget } = calculateEnergyDemandProjection(sectorImpact);

  // Generate comparison data if a comparison scenario is selected
  const comparisonData = comparisonScenario
    ? calculateComparisonEnergyDemandProjection(comparisonScenario.scenario, comparisonScenario.variant)
    : null;

  const energyDemandData = {
    labels: ['2025', '2030', '2035', '2040', '2045', '2050'],
    datasets: [
      {
        label: 'Business as Usual Scenario',
        data: [1500, 1550, 1600, 1650, 1700, 1750, 1800],
        borderColor: DASHBOARD_COLORS.neutral.baseline.primary,
        backgroundColor: DASHBOARD_COLORS.neutral.baseline.light,
        tension: 0.3
      },
      {
        label: 'Your Scenario',
        data: projectionData,
        borderColor: getNetZeroColor('energyDemand', 'primary'),
        backgroundColor: getNetZeroColor('energyDemand', 'light'),
        tension: 0.3
      },
      // Conditionally add comparison scenario dataset
      ...(comparisonData ? [{
        label: `${comparisonScenario.scenario.charAt(0).toUpperCase() + comparisonScenario.scenario.slice(1)} (${comparisonScenario.variant} ambition)`,
        data: comparisonData.projectionData,
        borderColor: '#8b5cf6', // Purple color for comparison
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        tension: 0.3,
        borderDash: [5, 5] // Dashed line to distinguish comparison
      }] : [])
    ]
  };

  const energyDemandOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Energy Demand Projection (2025-2050)'
      }
    },
    scales: {
      y: {
        title: {
          display: true,
          text: 'Yearly Energy Demand (TWh)'
        },
        beginAtZero: false,
        min: 300,  // Set the minimum value to 300
        suggestedMax: 1800  // Optional: adjust the maximum if needed
      },
      x: {
        title: {
          display: true,
          text: 'Year'
        }
      }
    }
  };

  // Emissions Reduction Graph Configuration
  const { emissionsProjectionData, netZeroEmissionsTarget } = calculateEmissionsReductionProjection(sectorImpact);

  const emissionsReductionData = {
    labels: ['2025', '2030', '2035', '2040', '2045', '2050'],
    datasets: [
      {
        label: 'Business as Usual Scenario',
        data: [450, 440, 430, 420, 410, 400, 390],
        borderColor: DASHBOARD_COLORS.neutral.baseline.primary,
        backgroundColor: DASHBOARD_COLORS.neutral.baseline.light,
        tension: 0.3
      },
      {
        label: 'Your Scenario',
        data: emissionsProjectionData,
        borderColor: getNetZeroColor('emissions', 'primary'),
        backgroundColor: getNetZeroColor('emissions', 'light'),
        tension: 0.3
      },
      {
        label: '2050 Net Zero Target',
        data: [45, 45, 45, 45, 45, 45, 45], // Constant net zero target
        borderColor: getNetZeroColor('greenEnergy', 'primary'),
        borderDash: [5, 5],
        borderWidth: 2,
        fill: false,
        pointRadius: 0
      }
    ]
  };

  const emissionsReductionOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Emissions Reduction Projection (2025-2050)'
      }
    },
    scales: {
      y: {
        title: {
          display: true,
          text: 'Yearly Emissions (MtCO₂/year)'
        },
        beginAtZero: false
      },
      x: {
        title: {
          display: true,
          text: 'Year'
        }
      }
    }
  };

  // Calculate emissions reduction percentage
  const emissionsReductionPercentage = calculateEmissionsReductionPercentage(sectorImpact);

  // Calculate energy demand reduction percentage
  const energyDemandReductionPercentage = calculateEnergyDemandReductionPercentage(sectorImpact);

  // Net Zero Balance Calculation
  const calculateNetZeroBalance = (sectorImpact) => {
    // UK Emissions Targets and Baseline
    // eslint-disable-next-line no-unused-vars
    const baselineEmissions = 450; // MtCO₂/year in 2020
    const interimTargets = {
      2030: 0.68, // 68% reduction by 2030
      2037: 0.78, // 78% reduction by 2037
      2050: 0.90  // 90% reduction by 2050
    };
    // eslint-disable-next-line no-unused-vars
    const carbonRemovalTarget = 78; // Million tonnes of CO₂ to be removed annually by 2050

    // Detailed reduction mapping for each sector
    const sectorReductionMap = {
      food: {
        low: 0,
        high: 15
      },
      buildings: {
        low: 0,
        high: 20
      },
      transport: {
        low: 0,
        high: 25
      },
      industry: {
        low: 0,
        high: 15
      }
    };

    // Emissions reduction multipliers for each sector
    const sectorReductionMultipliers = {
      food: {
        current: 1.0,
        low: 0.85,
        high: 0.7
      },
      buildings: {
        current: 1.0,
        low: 0.75,
        high: 0.6
      },
      transport: {
        current: 1.0,
        low: 0.75,
        high: 0.5
      },
      industry: {
        current: 1.0,
        low: 0.8,
        high: 0.65
      }
    };

    // Calculate cumulative reduction factor
    const cumulativeReductionFactor = Object.keys(sectorReductionMultipliers).reduce((acc, sector) => {
      return acc * sectorReductionMultipliers[sector][sectorImpact[sector]];
    }, 1);

    // Calculate final emissions in 2050
    const finalEmissions = baselineEmissions * cumulativeReductionFactor;

    // Calculate percentage reduction
    const emissionsReductionPercentage = Math.round((1 - finalEmissions / baselineEmissions) * 100);

    // Calculate infrastructure and carbon removal needs based on reduction percentage
    const infrastructureNeed = Math.round(50 * (1 - emissionsReductionPercentage / 100));
    const carbonRemovalNeed = 100 - emissionsReductionPercentage - infrastructureNeed;

    return {
      emissionsReductionPercentage,
      infrastructureNeed,
      carbonRemovalNeed
    };
  };

  // Calculate Net Zero Balance
  const netZeroBalance = calculateNetZeroBalance(sectorImpact);

  // Net Zero Balance Graph Configuration
  const netZeroBalanceData = {
    labels: [' '],
    datasets: [
      {
        label: 'Emissions Reduction',
        data: [netZeroBalance.emissionsReductionPercentage],
        backgroundColor: getNetZeroColor('emissions', 'light'),
        borderColor: getNetZeroColor('emissions', 'primary'),
        borderWidth: 1
      },
      {
        label: 'Clean Electricity Supply',
        data: [netZeroBalance.infrastructureNeed],
        backgroundColor: getNetZeroColor('energyDemand', 'light'),
        borderColor: getNetZeroColor('energyDemand', 'primary'),
        borderWidth: 1
      },
      {
        label: 'Carbon Removal',
        data: [netZeroBalance.carbonRemovalNeed],
        backgroundColor: getNetZeroColor('carbonRemoval', 'light'),
        borderColor: getNetZeroColor('carbonRemoval', 'primary'),
        borderWidth: 1
      }
    ]
  };

  const netZeroBalanceOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: 'Net Zero Balance Components'
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.formattedValue}%`;
          }
        }
      },
      legend: {
        position: 'top'
      }
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: ' Breakdown of Net Zero Pathway Components'
        }
      },
      y: {
        stacked: true,
        title: {
          display: true,
          text: 'Contribution to Net Zero (%)'
        },
        min: 0,
        max: 100
      }
    }
  };

  // Dynamic Electricity Generation Calculation
  const calculatePowerGenerationNeeds = (sectorImpact) => {
    // Validate input
    if (!sectorImpact || typeof sectorImpact !== 'object') {
      console.error('Invalid sectorImpact input', sectorImpact);
      return {
        offshoreWindFarms: {
          count: 12,
          details: {
            turbinesPerFarm: 100,
            totalTurbines: 1200,
            capacityMW: 1200,
            landArea: 300
          }
        },
        solarFarms: {
          count: 8,
          details: {
            solarPanelsPerFarm: 500000,
            totalSolarPanels: 4000000,
            capacityMW: 800,
            landArea: 120
          }
        },
        tidalEnergyProjects: {
          count: 4,
          details: {
            tidalTurbinesPerProject: 50,
            totalTidalTurbines: 200,
            capacityMW: 400,
            landArea: 50
          }
        },
        hydropowerPlants: {
          count: 3,
          details: {
            generatorsPerPlant: 6,
            totalGenerators: 18,
            capacityMW: 300,
            landArea: 40
          }
        },
        biomassPowerStations: {
          count: 5,
          details: {
            boilersPerStation: 4,
            totalBoilers: 20,
            capacityMW: 250,
            landArea: 30
          }
        },
        nuclearMicroPlants: {
          count: 2,
          details: {
            reactorsPerPlant: 4,
            totalReactors: 8,
            capacityMW: 600,
            landArea: 20
          }
        },
        totalInvestment: 24500
      };
    }

    // Ensure all sectors have a valid impact
    const safeSectorImpact = {
      food: ['high', 'low', 'current'].includes(sectorImpact.food) ? sectorImpact.food : 'current',
      buildings: ['high', 'low', 'current'].includes(sectorImpact.buildings) ? sectorImpact.buildings : 'current',
      transport: ['high', 'low', 'current'].includes(sectorImpact.transport) ? sectorImpact.transport : 'current',
      industry: ['high', 'low', 'current'].includes(sectorImpact.industry) ? sectorImpact.industry : 'current'
    };

    // Base infrastructure needs with more specific system details
    const baseInfrastructure = {
      offshoreWindFarms: {
        baseCount: 12,
        details: {
          turbinesPerFarm: 100,
          capacityMW: 1200,
          landArea: 300 // km²
        }
      },
      solarFarms: {
        baseCount: 8,
        details: {
          panelsPerFarm: 500000,
          capacityMW: 800,
          landArea: 120 // km²
        }
      },
      tidalEnergyProjects: {
        baseCount: 4,
        details: {
          turbinesPerProject: 50,
          capacityMW: 400,
          landArea: 50 // km²
        }
      },
      hydropowerPlants: {
        baseCount: 3,
        details: {
          turbinesPerPlant: 10,
          capacityMW: 300,
          landArea: 40 // km²
        }
      },
      biomassPowerStations: {
        baseCount: 5,
        details: {
          generatorsPerStation: 4,
          capacityMW: 250,
          landArea: 30 // km²
        }
      }
    };

    // Multipliers based on sector impacts
    const infrastructureMultipliers = {
      offshoreWindFarms: {
        current: 1.0,
        low: 0.8,
        high: 0.6
      },
      solarFarms: {
        current: 1.0,
        low: 0.85,
        high: 0.7
      },
      tidalEnergyProjects: {
        current: 1.0,
        low: 0.9,
        high: 0.8
      },
      hydropowerPlants: {
        current: 1.0,
        low: 0.9,
        high: 0.8
      },
      biomassPowerStations: {
        current: 1.0,
        low: 0.85,
        high: 0.7
      }
    };

    // Calculate infrastructure needs based on sector impacts
    const powerGenerationNeeds = {};
    let totalLandArea = 0;
    let totalCapacity = 0;

    Object.keys(baseInfrastructure).forEach(source => {
      // Determine reduction based on sector impacts
      const reductionFactors = [
        safeSectorImpact.food === 'high' ? 0.9 :
        safeSectorImpact.food === 'low' ? 0.95 : 1.0,
        safeSectorImpact.buildings === 'high' ? 0.9 :
        safeSectorImpact.buildings === 'low' ? 0.95 : 1.0,
        safeSectorImpact.transport === 'high' ? 0.9 :
        safeSectorImpact.transport === 'low' ? 0.95 : 1.0,
        safeSectorImpact.industry === 'high' ? 0.9 :
        safeSectorImpact.industry === 'low' ? 0.95 : 1.0
      ];

      // Calculate cumulative reduction factor
      const cumulativeReductionFactor = reductionFactors.reduce((a, b) => a * b, 1);

      // Calculate needed infrastructure
      const baseCount = baseInfrastructure[source].baseCount;
      const infrastructureMultiplier = infrastructureMultipliers[source][safeSectorImpact.transport];

      const adjustedCount = Math.round(baseCount * infrastructureMultiplier * cumulativeReductionFactor);

      powerGenerationNeeds[source] = {
        count: adjustedCount,
        details: {
          ...baseInfrastructure[source].details,
          totalTurbines: adjustedCount * baseInfrastructure[source].details.turbinesPerFarm,
          totalCapacityMW: adjustedCount * baseInfrastructure[source].details.capacityMW,
          totalLandArea: adjustedCount * baseInfrastructure[source].details.landArea
        }
      };

      // Accumulate total land area and capacity
      totalLandArea += powerGenerationNeeds[source].details.totalLandArea;
      totalCapacity += powerGenerationNeeds[source].details.totalCapacityMW;
    });

    // Add nuclear micro plants to the calculation
    const nuclearMultipliers = {
      current: 1.0,
      low: 0.9,
      high: 0.8
    };

    // Calculate nuclear micro plant needs based on sector impacts
    const baseNuclearCount = 2;
    const nuclearImpactFactor = [
      sectorImpact.food === 'high' ? 0.9 :
      sectorImpact.food === 'low' ? 0.95 : 1.0,
      sectorImpact.buildings === 'high' ? 0.9 :
      sectorImpact.buildings === 'low' ? 0.95 : 1.0,
      sectorImpact.transport === 'high' ? 0.9 :
      sectorImpact.transport === 'low' ? 0.95 : 1.0,
      sectorImpact.industry === 'high' ? 0.9 :
      sectorImpact.industry === 'low' ? 0.95 : 1.0
    ];

    const cumulativeReductionFactor = nuclearImpactFactor.reduce((a, b) => a * b, 1);
    const nuclearMultiplier = nuclearMultipliers[sectorImpact.transport];

    const adjustedNuclearCount = Math.round(baseNuclearCount * nuclearMultiplier * cumulativeReductionFactor);

    powerGenerationNeeds.nuclearMicroPlants = {
      count: adjustedNuclearCount,
      details: {
        reactorsPerPlant: 4,
        totalReactors: adjustedNuclearCount * 4,
        capacityMW: adjustedNuclearCount * 300,
        landArea: adjustedNuclearCount * 10
      }
    };

    // Update total investment and land area
    totalLandArea += powerGenerationNeeds.nuclearMicroPlants.details.landArea;
    totalCapacity += powerGenerationNeeds.nuclearMicroPlants.details.capacityMW;

    // Recalculate total investment
    const totalInvestment = Math.round(totalCapacity * 50 + totalLandArea * 100);

    const result = {
      ...powerGenerationNeeds,
      offshoreWindFarms: {
        ...powerGenerationNeeds.offshoreWindFarms,
        details: {
          ...powerGenerationNeeds.offshoreWindFarms.details,
          totalTurbines: powerGenerationNeeds.offshoreWindFarms.count * 100
        }
      },
      solarFarms: {
        ...powerGenerationNeeds.solarFarms,
        details: {
          ...powerGenerationNeeds.solarFarms.details,
          totalSolarPanels: powerGenerationNeeds.solarFarms.count * 500000
        }
      },
      tidalEnergyProjects: {
        ...powerGenerationNeeds.tidalEnergyProjects,
        details: {
          ...powerGenerationNeeds.tidalEnergyProjects.details,
          totalTidalTurbines: powerGenerationNeeds.tidalEnergyProjects.count * 50
        }
      },
      hydropowerPlants: {
        ...powerGenerationNeeds.hydropowerPlants,
        details: {
          ...powerGenerationNeeds.hydropowerPlants.details,
          totalGenerators: powerGenerationNeeds.hydropowerPlants.count * 6
        }
      },
      biomassPowerStations: {
        ...powerGenerationNeeds.biomassPowerStations,
        details: {
          ...powerGenerationNeeds.biomassPowerStations.details,
          totalBoilers: powerGenerationNeeds.biomassPowerStations.count * 4
        }
      },
      nuclearMicroPlants: {
        ...powerGenerationNeeds.nuclearMicroPlants,
        details: {
          ...powerGenerationNeeds.nuclearMicroPlants.details,
          totalReactors: powerGenerationNeeds.nuclearMicroPlants.count * 4
        }
      },
      totalInvestment,
      totalLandArea,
      totalCapacityMW: totalCapacity
    };

    return result;
  };

  // Calculate electricity generation needs
  const powerGenerationNeeds = calculatePowerGenerationNeeds(sectorImpact);

  // Calculate electricity generation context
  const powerGenerationContext = calculatePowerGenerationContext(
    sectorImpact,
    powerGenerationNeeds.totalCapacityMW
  );

  // Calculate investment details
  const investmentDetails = calculateInvestment();

  // Function to calculate home electricity demand in years for different scenarios
  const calculateElectricityYearsComparison = (scenario, variant) => {
    const scenarioConfig = getScenarioConfig(scenario, variant);

    // Use the same multipliers as in calculatePowerGenerationContext
    const sectorMultipliers = {
      food: {
        current: 1.0,
        low: 0.9,
        high: 0.8
      },
      buildings: {
        current: 1.0,
        low: 0.9,
        high: 0.7
      }
    };

    // Get the multipliers for this scenario, with fallback to current
    const foodMultiplier = sectorMultipliers.food[scenarioConfig.food] || sectorMultipliers.food.current;
    const buildingsMultiplier = sectorMultipliers.buildings[scenarioConfig.buildings] || sectorMultipliers.buildings.current;

    // Calculate cumulative reduction factor for the scenario
    const cumulativeReductionFactor = foodMultiplier * buildingsMultiplier;

    // Base electricity calculation (from powerGenerationContext)
    const currentAnnualGeneration = 292.7; // TWh
    // Use only generation, not imports, as per the requirement to show "annual 292.7 terawatt-hours"
    const totalCurrentElectricity = currentAnnualGeneration; // TWh

    // Calculate adjusted electricity for this scenario
    const adjustedElectricity = totalCurrentElectricity * cumulativeReductionFactor; // TWh

    // Calculate years of home electricity demand
    // Average UK home electricity consumption: 3,500 kWh per year
    const avgHomeElectricityConsumption = 3500; // kWh per year

    // Convert TWh to kWh: TWh * 1,000,000,000,000
    const adjustedElectricityKWh = adjustedElectricity * 1000000000000;

    // Calculate how many years this would power your home
    const yearsOfElectricity = Math.round(adjustedElectricityKWh / avgHomeElectricityConsumption);


    return {
      yearsOfElectricity,
      scenario,
      variant,
      adjustedElectricity,
      cumulativeReductionFactor
    };
  };

  // Generate comparison data for electricity demand projection if a comparison scenario is selected
  const electricityComparisonData = electricityComparisonScenario
    ? calculateComparisonPowerGenerationContext(electricityComparisonScenario.scenario, electricityComparisonScenario.variant)
    : null;

  // Calculate the three electricity years comparison values
  const currentScenarioYears = calculateElectricityYearsComparison('current', 'low');

  // Determine current scenario based on sector impact pattern
  const getCurrentScenarioName = () => {
    const highImpactSectors = Object.values(sectorImpact).filter(impact => impact === 'high').length;
    if (highImpactSectors === 0) return 'current';
    if (highImpactSectors <= 1) return 'steer';
    if (highImpactSectors <= 2) return 'shift';
    return 'transform';
  };

  const selectedScenarioYears = calculateElectricityYearsComparison(getCurrentScenarioName(), ambitionLevel);
  const comparisonScenarioYears = calculateElectricityYearsComparison(electricityYearsComparisonScenario.scenario, electricityYearsComparisonScenario.variant);

  // Function to calculate sector-specific emissions reduction over time for individual sector graphs
  const calculateIndividualSectorEmissionsReduction = (sectorName, sectorImpact) => {
    const years = ['2025', '2030', '2035', '2040', '2045', '2050'];
    const sectorKey = sectorName.toLowerCase();

    // Base emissions for each sector (in Mt CO2e)
    const baseEmissions = {
      food: 180,
      buildings: 75,
      transport: 110,
      industry: 95
    };

    const currentEmissions = baseEmissions[sectorKey] || 100;

    // Get sector-specific reduction based on impact level
    const impactLevels = {
      low: 0.65,     // 35% reduction by 2050
      medium: 0.45,  // 55% reduction by 2050
      high: 0.25     // 75% reduction by 2050
    };

    const sectorImpactLevel = sectorImpact[sectorKey] || 'low';
    const targetReduction = impactLevels[sectorImpactLevel] || impactLevels.low;

    return years.map((year, index) => {
      const progressFactor = index / (years.length - 1); // 0 to 1 over time
      const reduction = 1 - (targetReduction + (1 - targetReduction) * Math.pow(1 - progressFactor, 2));
      return Math.round(currentEmissions * (1 - reduction * progressFactor));
    });
  };

  // Function to calculate sector-specific energy demand with multiple energy vectors
  const calculateSectorEnergyDemandByVector = (sectorName, sectorImpact) => {
    const years = ['2025', '2030', '2035', '2040', '2045', '2050'];
    const sectorKey = sectorName.toLowerCase();

    // 2025 baseline energy mix by sector (realistic starting points)
    const baseline2025 = {
      food: {
        total: 60,
        fossil: 42,      // 70% fossil (gas for heating, diesel for machinery)
        electricity: 15, // 25% electricity
        hydrogen: 3      // 5% emerging hydrogen
      },
      buildings: {
        total: 180,
        fossil: 126,     // 70% fossil (gas heating)
        electricity: 45, // 25% electricity
        hydrogen: 9      // 5% hydrogen heating pilots
      },
      transport: {
        total: 150,
        fossil: 135,     // 90% fossil fuels (petrol/diesel)
        electricity: 12, // 8% electricity (EVs)
        hydrogen: 3      // 2% hydrogen vehicles
      },
      industry: {
        total: 140,
        fossil: 98,      // 70% fossil (gas, coal for high-temp processes)
        electricity: 35, // 25% electricity
        hydrogen: 7      // 5% hydrogen for steel, chemicals
      }
    };

    const sectorBaseline = baseline2025[sectorKey] || baseline2025.food;
    const sectorImpactLevel = sectorImpact[sectorKey] || 'low';

    // Map sector impact values to energy transition scenarios
    const impactMapping = {
      'low': 'low',
      'medium': 'medium',
      'high': 'high',
      'current': 'low',
      'steer': 'low',
      'shift': 'medium',
      'transform': 'high'
    };

    // Energy transition scenarios by impact level
    const energyTransitionScenarios = {
      low: {
        totalGrowth: 1.1,        // 10% total energy growth by 2050
        fossilReduction: 0.5,    // 50% fossil reduction by 2050
        electricityGrowth: 2.0,  // 100% electricity growth by 2050
        hydrogenGrowth: 4.0      // 300% hydrogen growth by 2050
      },
      medium: {
        totalGrowth: 1.2,        // 20% total energy growth by 2050
        fossilReduction: 0.7,    // 70% fossil reduction by 2050
        electricityGrowth: 3.0,  // 200% electricity growth by 2050
        hydrogenGrowth: 6.0      // 500% hydrogen growth by 2050
      },
      high: {
        totalGrowth: 1.3,        // 30% total energy growth by 2050
        fossilReduction: 0.85,   // 85% fossil reduction by 2050
        electricityGrowth: 4.0,  // 300% electricity growth by 2050
        hydrogenGrowth: 8.0      // 700% hydrogen growth by 2050
      }
    };

    const mappedLevel = impactMapping[sectorImpactLevel] || 'low';
    const scenario = energyTransitionScenarios[mappedLevel];

    return years.map((year, index) => {
      const progressFactor = index / (years.length - 1); // 0 to 1 over time
      const transitionCurve = Math.pow(progressFactor, 1.2); // Gradual then accelerating

      // Calculate total energy demand growth (due to increased activity/electrification)
      const totalGrowthFactor = 1 + (scenario.totalGrowth - 1) * transitionCurve;
      const newTotalDemand = sectorBaseline.total * totalGrowthFactor;

      // Calculate fossil fuel decline (absolute reduction despite total growth)
      const fossilDeclineFactor = 1 - (scenario.fossilReduction * transitionCurve);
      const fossilDemand = Math.max(0, sectorBaseline.fossil * fossilDeclineFactor);

      // Calculate electricity growth (replaces fossil + grows with new demand)
      const electricityGrowthFactor = 1 + (scenario.electricityGrowth - 1) * transitionCurve;
      const electricityDemand = sectorBaseline.electricity * electricityGrowthFactor;

      // Calculate hydrogen growth (new applications + replacing some fossil)
      const hydrogenGrowthFactor = 1 + (scenario.hydrogenGrowth - 1) * transitionCurve;
      const hydrogenDemand = sectorBaseline.hydrogen * hydrogenGrowthFactor;

      // Ensure the vectors sum close to total demand, with slight adjustment
      const vectorSum = fossilDemand + electricityDemand + hydrogenDemand;
      const adjustmentFactor = newTotalDemand / vectorSum;

      return {
        fossil: Math.round(fossilDemand * adjustmentFactor),
        electricity: Math.round(electricityDemand * adjustmentFactor),
        hydrogen: Math.round(hydrogenDemand * adjustmentFactor)
      };
    });
  };

  // Function to calculate other electricity contexts
  const calculateElectricityContexts = (adjustedElectricity) => {
    // Convert TWh to kWh
    const adjustedElectricityKWh = adjustedElectricity * 1000000000000;

    // UK households context: How long can we power ALL UK homes continuously?
    const ukHouseholds = 28000000; // 28 million UK homes
    const avgHouseholdConsumption = 3500; // kWh per year per household
    const totalUKHouseholdConsumption = ukHouseholds * avgHouseholdConsumption; // Total kWh needed for all UK homes per year
    const ukHouseholdsYears = Math.round(adjustedElectricityKWh / totalUKHouseholdConsumption);

    // Greater Manchester context: How long can we power Greater Manchester continuously?
    const greaterManchesterPopulation = 2900000; // 2.9 million people
    const perCapitaElectricityConsumption = 4500; // kWh per person per year (including all sectors)
    const totalGreaterManchesterConsumption = greaterManchesterPopulation * perCapitaElectricityConsumption;
    const greaterManchesterYears = Math.round(adjustedElectricityKWh / totalGreaterManchesterConsumption);

    // Rock concert context: How many years could a single concert run continuously?
    const concertDailyConsumption = 10000; // kWh per day for a single show
    const concertAnnualConsumption = concertDailyConsumption * 365; // kWh per year for continuous operation
    const concertYearsExact = adjustedElectricityKWh / concertAnnualConsumption;
    // Round to nearest thousand for readability of very large numbers
    const concertYears = Math.round(concertYearsExact / 1000) * 1000;

    // Fridges per household context: How many fridges each household could run continuously for a year
    const fridgeConsumption = 500; // kWh per year per fridge
    const totalHouseholds = 28400000; // 28.4 million households
    const totalElectricityPerHousehold = adjustedElectricityKWh / totalHouseholds; // kWh available per household
    const fridgesPerHousehold = Math.round(totalElectricityPerHousehold / fridgeConsumption);

    // Toast slices per person per day context
    const toastSliceConsumption = 0.02; // kWh per slice
    const ukPopulation = 67000000; // 67 million people
    const dailyToastSlicesPerPerson = adjustedElectricityKWh / (toastSliceConsumption * ukPopulation * 365);
    const toastSlicesPerPersonPerDay = Math.round(dailyToastSlicesPerPerson);

    // Kettles per person per day context
    const kettleBoilConsumption = 0.125; // kWh per kettle boil (typical 2.5kW kettle for 3 minutes)
    const dailyKettleBoilsPerPerson = adjustedElectricityKWh / (kettleBoilConsumption * ukPopulation * 365);
    const kettleBoilsPerPersonPerDay = Math.round(dailyKettleBoilsPerPerson);

    return {
      ukHouseholdsYears,
      greaterManchesterYears,
      concertYears,
      fridgesPerHousehold,
      toastSlicesPerPersonPerDay,
      kettleBoilsPerPersonPerDay
    };
  };

  // Calculate contexts for all three scenarios
  const currentContexts = calculateElectricityContexts(currentScenarioYears.adjustedElectricity);
  const selectedContexts = calculateElectricityContexts(selectedScenarioYears.adjustedElectricity);
  const comparisonContexts = calculateElectricityContexts(comparisonScenarioYears.adjustedElectricity);

  // Add a helper method to get comparison by name
  const getComparisonByName = (name) => {
    return powerGenerationContext.comparisons.find(comp =>
      comp.name.toLowerCase().replace(/\s/g, '') === name.toLowerCase().replace(/\s/g, '')
    );
  };

  // Update the top-level generation overview

  // Fallback for electricity generation needs
  const safePowerGenerationNeeds = {
    offshoreWindFarms: powerGenerationNeeds.offshoreWindFarms || {
      count: 12,
      details: {
        turbinesPerFarm: 100,
        totalTurbines: 1200,
        capacityMW: 1200,
        landArea: 300
      }
    },
    solarFarms: powerGenerationNeeds.solarFarms || {
      count: 8,
      details: {
        solarPanelsPerFarm: 500000,
        totalSolarPanels: 4000000,
        capacityMW: 800,
        landArea: 120
      }
    },
    tidalEnergyProjects: powerGenerationNeeds.tidalEnergyProjects || {
      count: 4,
      details: {
        tidalTurbinesPerProject: 50,
        totalTidalTurbines: 200,
        capacityMW: 400,
        landArea: 50
      }
    },
    hydropowerPlants: powerGenerationNeeds.hydropowerPlants || {
      count: 3,
      details: {
        generatorsPerPlant: 6,
        totalGenerators: 18,
        capacityMW: 300,
        landArea: 40
      }
    },
    biomassPowerStations: powerGenerationNeeds.biomassPowerStations || {
      count: 5,
      details: {
        boilersPerStation: 4,
        totalBoilers: 20,
        capacityMW: 250,
        landArea: 30
      }
    },
    nuclearMicroPlants: powerGenerationNeeds.nuclearMicroPlants || {
      count: 2,
      details: {
        reactorsPerPlant: 4,
        totalReactors: 8,
        capacityMW: 600,
        landArea: 20
      }
    },
    totalInvestment: powerGenerationNeeds.totalInvestment || 24500
  };

  // Log the final safe needs

  // Calculate land area comparison with Manchester
  const calculateLandAreaComparison = (totalLandArea) => {
    const manchesterMetroArea = 1276; // square kilometers
    const landAreaPercentage = Math.round((totalLandArea / manchesterMetroArea) * 100);

    return {
      totalLandArea,
      manchesterComparison: {
        percentage: landAreaPercentage,
        description: landAreaPercentage < 1
          ? "Less than 1% of Greater Manchester's metropolitan area"
          : landAreaPercentage < 10
            ? `Approximately ${landAreaPercentage}% of Greater Manchester's metropolitan area`
            : landAreaPercentage >= 10 && landAreaPercentage < 50
              ? `About half the size of Greater Manchester's metropolitan area`
              : landAreaPercentage >= 50 && landAreaPercentage < 100
                ? `Nearly the same size as Greater Manchester's metropolitan area`
                : `Larger than Greater Manchester's entire metropolitan area`
      }
    };
  };

  // Calculate land area for electricity generation
  const landAreaDetails = calculateLandAreaComparison(
    Math.round(safePowerGenerationNeeds.totalInvestment / 20.5)
  );

  // UK National Energy Transition Investment Calculation
  const calculateNationalInvestmentComparison = () => {
    // 2023 UK national energy transition investment
    const nationalInvestment = 57_800_000_000; // £57.8 billion

    // Government anticipated annual spending for net zero (fixed value)
    const governmentNetZeroAnnualSpending = 4_000_000_000; // £4 billion

    // Estimated additional investment needed annually to achieve net zero
    const additionalInvestmentNeededLow = 32_000_000_000; // £32 billion
    const additionalInvestmentNeededHigh = 174_000_000_000; // £174 billion

    // Estimate number of households in the UK (as of 2023)
    const ukHouseholds = 28_000_000; // Approximately 28 million households

    // Calculate investment metrics
    const nationalInvestmentPerHousehold = Math.round(nationalInvestment / ukHouseholds);
    const governmentNetZeroSpendPerHousehold = Math.round(governmentNetZeroAnnualSpending / ukHouseholds);
    const additionalInvestmentPerHouseholdLow = Math.round(additionalInvestmentNeededLow / ukHouseholds);
    const additionalInvestmentPerHouseholdHigh = Math.round(additionalInvestmentNeededHigh / ukHouseholds);

    // Calculate total additional investment per household over 25 years
    const totalAdditionalInvestmentPerHousehold = Math.round(additionalInvestmentNeededHigh / ukHouseholds);
    const annualAdditionalInvestmentPerHousehold = Math.round(totalAdditionalInvestmentPerHousehold / 25);

    return {
      nationalInvestment,
      governmentNetZeroAnnualSpending,
      additionalInvestmentNeededLow,
      additionalInvestmentNeededHigh,
      ukHouseholds,
      nationalInvestmentPerHousehold,
      governmentNetZeroSpendPerHousehold,
      additionalInvestmentPerHouseholdLow,
      additionalInvestmentPerHouseholdHigh,
      totalAdditionalInvestmentPerHousehold,
      annualAdditionalInvestmentPerHousehold
    };
  };

  // Calculate national investment comparison
  const nationalInvestmentDetails = calculateNationalInvestmentComparison();

  // Dynamic Carbon Removal Calculation
  const calculateCarbonRemovalNeeds = (sectorImpact) => {
    // Base carbon removal capacity of the UK (estimated)
    const ukMaxCarbonRemovalCapacity = 160; // MtCO₂/year

    // Base carbon removal needs
    const baseRemovalNeeds = 120; // MtCO₂/year

    // Sector impact multipliers for carbon removal
    const sectorRemovalMultipliers = {
      food: {
        current: 1.0,
        low: 0.9,
        high: 0.7
      },
      buildings: {
        current: 1.0,
        low: 0.9,
        high: 0.8
      },
      transport: {
        current: 1.0,
        low: 0.9,
        high: 0.6
      },
      industry: {
        current: 1.0,
        low: 0.9,
        high: 0.9
      }
    };

    // Validate input
    const safeSectorImpact = {
      food: ['high', 'low', 'current'].includes(sectorImpact.food) ? sectorImpact.food : 'current',
      buildings: ['high', 'low', 'current'].includes(sectorImpact.buildings) ? sectorImpact.buildings : 'current',
      transport: ['high', 'low', 'current'].includes(sectorImpact.transport) ? sectorImpact.transport : 'current',
      industry: ['high', 'low', 'current'].includes(sectorImpact.industry) ? sectorImpact.industry : 'current'
    };

    // Calculate cumulative reduction factor
    const cumulativeReductionFactor = Object.keys(sectorRemovalMultipliers).reduce((acc, sector) => {
      const sectorFactor = sectorRemovalMultipliers[sector][safeSectorImpact[sector]];
    //   console.log(`Sector: ${sector}, Impact: ${safeSectorImpact[sector]}, Removal Factor: ${sectorFactor}`);
      return acc * sectorFactor;
    }, 1);

    // Calculate dynamic carbon removal needs
    const dynamicRemovalNeeds = Math.round(baseRemovalNeeds * cumulativeReductionFactor);

    // Calculate percentage of UK's maximum carbon removal capacity
    const removalCapacityPercentage = Math.round((dynamicRemovalNeeds / ukMaxCarbonRemovalCapacity) * 100);


    return {
      totalRemovalNeeds: dynamicRemovalNeeds,
      ukMaxCarbonRemovalCapacity,
      removalCapacityPercentage
    };
  };

  // Calculate carbon removal needs
  const carbonRemovalDetails = calculateCarbonRemovalNeeds(sectorImpact);

  // Dynamic Sector Impact Calculation
  const calculateSectorImpactDetails = (sectorImpact) => {
    // Validate input
    const safeSectorImpact = {
      food: ['current', 'low', 'high'].includes(sectorImpact.food) ? sectorImpact.food : 'current',
      buildings: ['current', 'low', 'high'].includes(sectorImpact.buildings) ? sectorImpact.buildings : 'current',
      transport: ['current', 'low', 'high'].includes(sectorImpact.transport) ? sectorImpact.transport : 'current',
      industry: ['current', 'low', 'high'].includes(sectorImpact.industry) ? sectorImpact.industry : 'current'
    };

    // Base impact values for each sector
    const baseImpacts = {
      food: {
        current: 10,  // MtCO₂/year
        low: 5,       // MtCO₂/year
        high: 15      // MtCO₂/year
      },
      buildings: {
        current: 12,  // MtCO₂/year
        low: 8,       // MtCO₂/year
        high: 20      // MtCO₂/year
      },
      transport: {
        current: 15,  // MtCO₂/year
        low: 12,      // MtCO₂/year
        high: 35      // MtCO₂/year
      },
      industry: {
        current: 10,  // MtCO₂/year
        low: 6,       // MtCO₂/year
        high: 15      // MtCO₂/year
      }
    };

    // Calculate total impacts and percentages
    const sectorImpactDetails = {};
    let totalImpact = 0;

    Object.keys(baseImpacts).forEach(sector => {
      // Use the impact value for the current sector's state
      const impact = baseImpacts[sector][safeSectorImpact[sector]];

      sectorImpactDetails[sector] = {
        totalImpact: impact,
        impactPercentage: null  // Will be calculated after total
      };

      totalImpact += impact;
    });

    // Calculate percentages
    Object.keys(sectorImpactDetails).forEach(sector => {
      sectorImpactDetails[sector].impactPercentage =
        Math.round((sectorImpactDetails[sector].totalImpact / totalImpact) * 100);
    });

    return sectorImpactDetails;
  };

  // Calculate sector impact details
  const dynamicSectorImpactDetails = calculateSectorImpactDetails(sectorImpact);

  // Sector-Specific Emissions Reduction Calculation
  const calculateSectorEmissionsReduction = (sectorImpact) => {
    // Base emissions for each sector in 2020
    const baseEmissions = {
      food: 35,       // MtCO₂/year
      buildings: 40,  // MtCO₂/year
      transport: 50,  // MtCO₂/year
      industry: 45    // MtCO₂/year
    };

    // Reduction multipliers for each sector and impact state
    const reductionMultipliers = {
      food: {
        current: 1.0,
        low: 0.85,
        high: 0.7
      },
      buildings: {
        current: 1.0,
        low: 0.8,
        high: 0.6
      },
      transport: {
        current: 1.0,
        low: 0.75,
        high: 0.5
      },
      industry: {
        current: 1.0,
        low: 0.85,
        high: 0.65
      }
    };

    // Validate input
    const safeSectorImpact = {
      food: ['current', 'low', 'high'].includes(sectorImpact.food) ? sectorImpact.food : 'current',
      buildings: ['current', 'low', 'high'].includes(sectorImpact.buildings) ? sectorImpact.buildings : 'current',
      transport: ['current', 'low', 'high'].includes(sectorImpact.transport) ? sectorImpact.transport : 'current',
      industry: ['current', 'low', 'high'].includes(sectorImpact.industry) ? sectorImpact.industry : 'current'
    };

    // Calculate sector-specific emissions reduction projections
    const sectorEmissionsReduction = {};

    Object.keys(baseEmissions).forEach(sector => {
      const baseValue = baseEmissions[sector];
      const reductionFactor = reductionMultipliers[sector][safeSectorImpact[sector]];

      // Generate projection data points
      const projectionData = [
        baseValue,
        baseValue * (1 - (1 - reductionFactor) * 0.2),
        baseValue * (1 - (1 - reductionFactor) * 0.4),
        baseValue * (1 - (1 - reductionFactor) * 0.6),
        baseValue * (1 - (1 - reductionFactor) * 0.8),
        baseValue * reductionFactor,
        baseValue * reductionFactor
      ];

      sectorEmissionsReduction[sector] = {
        projectionData,
        reductionPercentage: Math.round((1 - reductionFactor) * 100)
      };
    });

    return sectorEmissionsReduction;
  };

  // Calculate sector-specific emissions reduction
  const sectorEmissionsReduction = calculateSectorEmissionsReduction(sectorImpact);

  // Sector Emissions Reduction Graph Configuration
  const createSectorEmissionsGraph = (sector) => {
    const sectorColors = {
      food: { border: 'rgb(255, 159, 64)', background: 'rgba(255, 159, 64, 0.2)' },
      buildings: { border: 'rgb(255, 99, 132)', background: 'rgba(255, 99, 132, 0.2)' },
      transport: { border: 'rgb(54, 162, 235)', background: 'rgba(54, 162, 235, 0.2)' },
      industry: { border: 'rgb(75, 192, 192)', background: 'rgba(75, 192, 192, 0.2)' }
    };

    return {
      labels: ['2025', '2030', '2035', '2040', '2045', '2050'],
      datasets: [
        {
          label: `${sector.charAt(0).toUpperCase() + sector.slice(1)} Sector Emissions`,
          data: sectorEmissionsReduction[sector].projectionData.slice(1), // Remove baseline
          borderColor: sectorColors[sector].border,
          backgroundColor: sectorColors[sector].background,
          tension: 0.3,
          fill: true
        }
      ]
    };
  };

  const sectorEmissionsGraphOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: 'Sector Emissions Reduction (2025-2050)'
      }
    },
    scales: {
      y: {
        title: {
          display: true,
          text: 'Emissions (Mt CO₂e/year)'
        },
        beginAtZero: false
      },
      x: {
        title: {
          display: true,
          text: 'Year'
        }
      }
    }
  };

  // Carbon Intensity Calculation Function
  const calculateCarbonIntensity = (sectorImpact) => {
    // Current UK electricity carbon intensity benchmarks (2023)
    const currentBenchmarks = {
      gas: 350, // gCO₂/kWh
      coal: 900, // gCO₂/kWh
      current_grid_mix: 200, // gCO₂/kWh
      nuclear: 12, // gCO₂/kWh
      offshore_wind: 10, // gCO₂/kWh
      solar: 40, // gCO₂/kWh
      biomass: 230 // gCO₂/kWh
    };

    // Sector impact multipliers for carbon intensity reduction
    const sectorIntensityMultipliers = {
      food: {
        current: 1.0,
        low: 0.9,
        high: 0.8
      },
      buildings: {
        current: 1.0,
        low: 0.85,
        high: 0.7
      },
      transport: {
        current: 1.0,
        low: 0.9,
        high: 0.6
      },
      industry: {
        current: 1.0,
        low: 0.85,
        high: 0.7
      }
    };

    // Validate input
    const safeSectorImpact = {
      food: ['current', 'low', 'high'].includes(sectorImpact.food) ? sectorImpact.food : 'current',
      buildings: ['current', 'low', 'high'].includes(sectorImpact.buildings) ? sectorImpact.buildings : 'current',
      transport: ['current', 'low', 'high'].includes(sectorImpact.transport) ? sectorImpact.transport : 'current',
      industry: ['current', 'low', 'high'].includes(sectorImpact.industry) ? sectorImpact.industry : 'current'
    };

    // Calculate cumulative reduction factor
    const cumulativeReductionFactor = Object.keys(sectorIntensityMultipliers).reduce((acc, sector) => {
      return acc * sectorIntensityMultipliers[sector][safeSectorImpact[sector]];
    }, 1);

    // Generate carbon intensity projection
    const baselineIntensity = currentBenchmarks.current_grid_mix;
    const carbonIntensityProjection = [
      { year: 2025, intensity: Math.round(baselineIntensity * (1 - (1 - cumulativeReductionFactor) * 0.2)) },
      { year: 2030, intensity: Math.round(baselineIntensity * (1 - (1 - cumulativeReductionFactor) * 0.4)) },
      { year: 2035, intensity: Math.round(baselineIntensity * (1 - (1 - cumulativeReductionFactor) * 0.6)) },
      { year: 2040, intensity: Math.round(baselineIntensity * (1 - (1 - cumulativeReductionFactor) * 0.8)) },
      { year: 2045, intensity: Math.round(baselineIntensity * (1 - (1 - cumulativeReductionFactor) * 0.9)) },
      { year: 2050, intensity: Math.round(baselineIntensity * cumulativeReductionFactor) }
    ];

    // Calculate percentage reduction
    const finalIntensity = carbonIntensityProjection[carbonIntensityProjection.length - 1].intensity;
    const intensityReductionPercentage = Math.round((1 - finalIntensity / baselineIntensity) * 100);

    return {
      currentBenchmarks,
      carbonIntensityProjection,
      intensityReductionPercentage,
      baselineIntensity,
      finalIntensity
    };
  };

  // Calculate carbon intensity details
  const carbonIntensityDetails = calculateCarbonIntensity(sectorImpact);

  // Generate comparison data for carbon intensity if a comparison scenario is selected
  const carbonIntensityComparisonData = carbonIntensityComparisonScenario
    ? calculateComparisonCarbonIntensity(carbonIntensityComparisonScenario.scenario, carbonIntensityComparisonScenario.variant)
    : null;

  // Function to determine current scenario from sector impacts
  const getCurrentScenario = () => {
    // Check each scenario configuration to find a match
    for (const [scenarioName, scenarioVariants] of Object.entries({
      [PANEL_SCENARIO_KEY]: {
        panel: PANEL_SECTOR_CONFIG
      },
      steer: {
        low: { food: 'current', buildings: 'current', transport: 'low', industry: 'low' },
        high: { food: 'current', buildings: 'low', transport: 'low', industry: 'low' }
      },
      shift: {
        low: { food: 'low', buildings: 'low', transport: 'high', industry: 'low' },
        high: { food: 'low', buildings: 'high', transport: 'low', industry: 'low' }
      },
      transform: {
        low: { food: 'low', buildings: 'high', transport: 'high', industry: 'low' },
        high: { food: 'high', buildings: 'high', transport: 'high', industry: 'high' }
      },
      current: {
        low: { food: 'current', buildings: 'current', transport: 'current', industry: 'current' },
        high: { food: 'current', buildings: 'current', transport: 'current', industry: 'current' }
      }
    })) {
      for (const [variant, config] of Object.entries(scenarioVariants)) {
        const isMatch = Object.entries(config).every(([sector, impact]) =>
          sectorImpact[sector] === impact
        );
        if (isMatch) {
          return { scenario: scenarioName, variant };
        }
      }
    }
    return { scenario: 'current', variant: 'low' };
  };

  // Get current scenario
  const currentScenarioInfo = getCurrentScenario();

  function scenarioKeyForBackend({ scenario, variant }) {
  // Handle special panel scenario
  if (scenario === PANEL_SCENARIO_KEY) {
    return PANEL_SCENARIO_KEY;
  }

  console.log(`Mapping scenario: ${scenario}, variant: ${variant}`);

const MAP = {
    current: {
      low:  "net_zero_futures_dvl_transform_low_0909",
      high: "net_zero_futures_dvl_transform_high_0909",
    },
    steer: {
      low:  "net_zero_futures_dvl_steer_low_0909",
      high: "net_zero_futures_dvl_steer_high_0909",
    },
    shift: {
      low:  "net_zero_futures_dvl_shift_low_0909",
      high: "net_zero_futures_dvl_shift_high_0909",
    },
    transform: {
      low:  "net_zero_futures_dvl_transform_low_0909",
      high: "net_zero_futures_dvl_transform_high_0909",
    }
  };


  return MAP[scenario]?.[variant] ?? null; // fall back to null if unknown
}
// const currentScenarioInfo = getCurrentScenario();
const scenarioKey = scenarioKeyForBackend(currentScenarioInfo);
console.log(scenarioKey );
  // Carbon Intensity Graph Configuration
  const carbonIntensityData = {
    labels: carbonIntensityDetails.carbonIntensityProjection.map(point => point.year),
    datasets: [
      {
        label: 'Your Scenario',
        data: carbonIntensityDetails.carbonIntensityProjection.map(point => point.intensity),
        borderColor: getNetZeroColor('greenEnergy', 'primary'),
        backgroundColor: getNetZeroColor('greenEnergy', 'light'),
        tension: 0.3,
        fill: true
      },
      {
        label: 'Current Grid Mix Benchmark',
        data: [
          carbonIntensityDetails.currentBenchmarks.current_grid_mix,
          carbonIntensityDetails.currentBenchmarks.current_grid_mix,
          carbonIntensityDetails.currentBenchmarks.current_grid_mix,
          carbonIntensityDetails.currentBenchmarks.current_grid_mix,
          carbonIntensityDetails.currentBenchmarks.current_grid_mix,
          carbonIntensityDetails.currentBenchmarks.current_grid_mix
        ],
        borderColor: 'rgb(255, 206, 86)',
        borderDash: [5, 5],
        borderWidth: 2,
        fill: false,
        pointRadius: 0
      },
      // Conditionally add comparison scenario dataset
      ...(carbonIntensityComparisonData ? [{
        label: `${carbonIntensityComparisonScenario.scenario.charAt(0).toUpperCase() + carbonIntensityComparisonScenario.scenario.slice(1)} (${carbonIntensityComparisonScenario.variant} ambition)`,
        data: carbonIntensityComparisonData.carbonIntensityProjection.map(point => point.intensity),
        borderColor: '#8b5cf6', // Purple color for comparison
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        tension: 0.3,
        borderDash: [5, 5], // Dashed line to distinguish comparison
        fill: false
      }] : [])
    ]
  };

  const carbonIntensityOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Electricity System Carbon Intensity Projection (2025-2050)'
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.formattedValue} gCO₂/kWh`;
          }
        }
      }
    },
    scales: {
      y: {
        title: {
          display: true,
          text: 'Carbon Intensity (gCO₂/kWh)'
        },
        beginAtZero: false,
        suggestedMin: 0,
        suggestedMax: 400
      },
      x: {
        title: {
          display: true,
          text: 'Year'
        }
      }
    }
  };

  // Carbon Removal Technology Projection Calculation
  // Function to calculate projected emissions reduction with sector breakdown
  const calculateEmissionsProjection = (sectorImpact) => {
    // Baseline UK emissions in 2020
    const baselineEmissions = 450;

    // Sector reduction multipliers with current, low, and high states
    const sectorMultipliers = {
      food: {
        current: 1.0,
        low: 0.85,
        high: 0.7
      },
      buildings: {
        current: 1.0,
        low: 0.75,
        high: 0.6
      },
      transport: {
        current: 1.0,
        low: 0.75,
        high: 0.5
      },
      industry: {
        current: 1.0,
        low: 0.8,
        high: 0.65
      }
    };

    // Validate input
    const safeSectorImpact = {
      food: sectorImpact.food || 'current',
      buildings: sectorImpact.buildings || 'current',
      transport: sectorImpact.transport || 'current',
      industry: sectorImpact.industry || 'current'
    };

    // Initial sector emissions based on baseline
    const initialSectorEmissions = {
      food: baselineEmissions * 0.25,
      buildings: baselineEmissions * 0.30,
      transport: baselineEmissions * 0.20,
      industry: baselineEmissions * 0.25
    };

    // Calculate individual sector emissions
    const sectorEmissions = {
      food: [],
      buildings: [],
      transport: [],
      industry: []
    };

    // Calculate sector-specific percentage reductions
    const sectorReductions = {};

    // Calculate cumulative reduction factor and sector-specific reductions
    const sectorReductionFactors = Object.keys(sectorMultipliers).reduce((acc, sector) => {
      const sectorFactor = sectorMultipliers[sector][safeSectorImpact[sector]];

      // Calculate percentage reduction
      const initialEmission = initialSectorEmissions[sector];
      const finalEmission = initialEmission * sectorFactor;
      const percentageReduction = Math.round((1 - sectorFactor) * 100);

      sectorReductions[sector] = {
        initial: initialEmission,
        final: finalEmission,
        percentageReduction: percentageReduction
      };

      acc[sector] = sectorFactor;
      return acc;
    }, {});

    // Generate projection data points for each sector
    const years = [2025, 2030, 2035, 2040, 2045, 2050];
    years.forEach((year, index) => {
      const progressFactor = [0.2, 0.4, 0.6, 0.8, 1, 1][index];

      // Calculate sector-specific emissions
      Object.keys(sectorEmissions).forEach(sector => {
        const initialEmission = initialSectorEmissions[sector];
        const reductionFactor = sectorReductionFactors[sector];

        // Calculate sector-specific emissions
        const sectorEmission = initialEmission * (1 - (1 - reductionFactor) * progressFactor);
        sectorEmissions[sector].push(sectorEmission);
      });
    });

    // Calculate total emissions for each year
    const projectionData = years.map((_, index) =>
      Object.values(sectorEmissions).reduce((total, sector) => total + sector[index], 0)
    );

    const finalEmissions = projectionData[projectionData.length - 1];
    const reductionPercentage = Math.round((1 - finalEmissions / baselineEmissions) * 100);


    return {
      projectionData,
      sectorEmissions,
      sectorReductions,
      finalEmissions,
      reductionPercentage
    };
  };

  const calculateCarbonRemovalTechnologiesProjection = (sectorImpact) => {
    // Base carbon removal capacity for each technology in the UK (estimated)
    const baseCarbonRemovalCapacities = {
      afforestation: {
        current: 0.5,  // MtCO₂/year in 2025
        maxPotential: 10  // Maximum potential by 2050
      },
      directAirCapture: {
        current: 0.05,  // MtCO₂/year in 2025
        maxPotential: 5  // Maximum potential by 2050
      },
      bioenergyCarbonCapture: {
        current: 0.3,  // MtCO₂/year in 2025
        maxPotential: 5  // Maximum potential by 2050
      }
    };

    // Sector impact multipliers for carbon removal technology development
    const sectorRemovalMultipliers = {
      food: {
        current: 1.0,
        low: 0.9,
        high: 0.8
      },
      buildings: {
        current: 1.0,
        low: 0.9,
        high: 0.7
      },
      transport: {
        current: 1.0,
        low: 0.9,
        high: 0.6
      },
      industry: {
        current: 1.0,
        low: 0.9,
        high: 0.7
      }
    };

    // Validate input
    const safeSectorImpact = {
      food: ['current', 'low', 'high'].includes(sectorImpact.food) ? sectorImpact.food : 'current',
      buildings: ['current', 'low', 'high'].includes(sectorImpact.buildings) ? sectorImpact.buildings : 'current',
      transport: ['current', 'low', 'high'].includes(sectorImpact.transport) ? sectorImpact.transport : 'current',
      industry: ['current', 'low', 'high'].includes(sectorImpact.industry) ? sectorImpact.industry : 'current'
    };

    // Calculate cumulative reduction factor for technology development
    const cumulativeReductionFactor = Object.keys(sectorRemovalMultipliers).reduce((acc, sector) => {
      return acc * sectorRemovalMultipliers[sector][safeSectorImpact[sector]];
    }, 1);

    // Generate projection data points for each technology
    const carbonRemovalProjections = {};
    const years = [2025, 2030, 2035, 2040, 2045, 2050];

    Object.keys(baseCarbonRemovalCapacities).forEach(technology => {
      const { current, maxPotential } = baseCarbonRemovalCapacities[technology];

      // Create a realistic S-curve growth projection
      const projectionData = years.map((year, index) => {
        // Use a sigmoid-like function to model technology development
        const progress = index / (years.length - 1);
        const growthFactor = Math.pow(progress, 2.5); // Non-linear growth

        // Apply sector impact reduction factor
        const adjustedMaxPotential = maxPotential * cumulativeReductionFactor;

        // Calculate the value using a smooth S-curve
        const value = current + (adjustedMaxPotential - current) * growthFactor;

        return Math.round(value * 10) / 10; // Round to 1 decimal place
      });

      carbonRemovalProjections[technology] = projectionData;
    });

    // Calculate total carbon removal potential
    const totalCarbonRemovalProjection = years.map((_, index) =>
      Object.values(carbonRemovalProjections).reduce((total, technology) => total + technology[index], 0)
    );



    return {
      carbonRemovalProjections,
      totalCarbonRemovalProjection
    };
  };

  // Calculate carbon removal technologies projection
  const carbonRemovalTechnologiesProjection = calculateCarbonRemovalTechnologiesProjection(sectorImpact);

  // Generate comparison data for carbon removal technologies if a comparison scenario is selected
  const carbonRemovalComparisonData = carbonRemovalComparisonScenario
    ? calculateComparisonCarbonRemovalTechnologies(carbonRemovalComparisonScenario.scenario, carbonRemovalComparisonScenario.variant)
    : null;

  // Calculate emissions projection for Overall Impact tab
  const emissionsProjection = calculateEmissionsProjection(sectorImpact);

  // Calculate total values for debugging
  const mainScenarioTotals = carbonRemovalTechnologiesProjection.carbonRemovalProjections.afforestation.map((afforest, index) =>
    afforest +
    carbonRemovalTechnologiesProjection.carbonRemovalProjections.directAirCapture[index] +
    carbonRemovalTechnologiesProjection.carbonRemovalProjections.bioenergyCarbonCapture[index]
  );

  const comparisonScenarioTotals = carbonRemovalComparisonData
    ? carbonRemovalComparisonData.carbonRemovalProjections.afforestation.map((afforest, index) =>
        afforest +
        carbonRemovalComparisonData.carbonRemovalProjections.directAirCapture[index] +
        carbonRemovalComparisonData.carbonRemovalProjections.bioenergyCarbonCapture[index]
      )
    : [];


  // Carbon Removal Technologies Graph Configuration
  const carbonRemovalTechnologiesData = {
    labels: ['2025', '2030', '2035', '2040', '2045', '2050'],
    datasets: [
      {
        type: 'line',
        label: 'Afforestation & Reforestation (Your Scenario)',
        data: carbonRemovalTechnologiesProjection.carbonRemovalProjections.afforestation,
        backgroundColor: getAbatementColor('natureBased', 'light'),
        borderColor: getAbatementColor('natureBased', 'primary'),
        fill: 'origin',
        tension: 0.3,
        pointRadius: 0,
        order: 3
      },
      {
        type: 'line',
        label: 'Direct Air Capture (Your Scenario)',
        data: carbonRemovalTechnologiesProjection.carbonRemovalProjections.directAirCapture.map((value, index) =>
          value + carbonRemovalTechnologiesProjection.carbonRemovalProjections.afforestation[index]
        ),
        backgroundColor: getAbatementColor('engineeredRemovals', 'light'),
        borderColor: getAbatementColor('engineeredRemovals', 'primary'),
        fill: '-1',
        tension: 0.3,
        pointRadius: 0,
        order: 2
      },
      {
        type: 'line',
        label: 'Bioenergy with Carbon Capture (Your Scenario)',
        data: mainScenarioTotals,
        backgroundColor: getAbatementColor('lowCarbonFuels', 'light'),
        borderColor: getAbatementColor('lowCarbonFuels', 'primary'),
        fill: '-1',
        tension: 0.3,
        pointRadius: 0,
        order: 1
      },
      // Add comparison scenario as a completely separate line
      ...(carbonRemovalComparisonData ? [
        {
          type: 'line',
          label: `Total Carbon Removal (${carbonRemovalComparisonScenario.scenario.charAt(0).toUpperCase() + carbonRemovalComparisonScenario.scenario.slice(1)} ${carbonRemovalComparisonScenario.variant})`,
          data: comparisonScenarioTotals,
          backgroundColor: 'transparent',
          borderColor: '#8b5cf6',
          borderDash: [8, 4],
          borderWidth: 3,
          fill: false,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 6,
          pointBackgroundColor: '#8b5cf6',
          pointBorderColor: '#8b5cf6',
          order: 0
        }
      ] : [])
    ]
  };

  const carbonRemovalTechnologiesOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    elements: {
      line: {
        tension: 0.3
      },
      point: {
        radius: 0,
        hoverRadius: 4
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          filter: function(legendItem) {
            // Show all legend items
            return true;
          }
        }
      },
      title: {
        display: true,
        text: 'Carbon Removal Technologies Projection (2025-2050)'
      },
              tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(context) {
              // Check if this is the comparison line (should be the last dataset when present)
              const isComparisonLine = carbonRemovalComparisonData && context.datasetIndex === 3;

              if (isComparisonLine) {
                // This is the comparison line
                return `${context.dataset.label}: ${context.parsed.y.toFixed(1)} Mt CO₂/year`;
              } else {
                // For stacked data, show individual technology contribution
                const technologiesOrder = ['Afforestation & Reforestation', 'Direct Air Capture', 'Bioenergy with Carbon Capture'];
                const techIndex = context.datasetIndex;
                if (techIndex < technologiesOrder.length) {
                  const techName = technologiesOrder[techIndex];
                  const rawData = techIndex === 0
                    ? carbonRemovalTechnologiesProjection.carbonRemovalProjections.afforestation[context.dataIndex]
                    : techIndex === 1
                      ? carbonRemovalTechnologiesProjection.carbonRemovalProjections.directAirCapture[context.dataIndex]
                      : carbonRemovalTechnologiesProjection.carbonRemovalProjections.bioenergyCarbonCapture[context.dataIndex];
                  return `${techName}: ${rawData.toFixed(1)} Mt CO₂/year`;
                }
                return `${context.dataset.label}: ${context.parsed.y.toFixed(1)} Mt CO₂/year`;
              }
            },
            afterBody: function(tooltipItems) {
              // Show total for the main scenario
              if (tooltipItems.length > 0) {
                const dataIndex = tooltipItems[0].dataIndex;
                const mainScenarioTotal =
                  carbonRemovalTechnologiesProjection.carbonRemovalProjections.afforestation[dataIndex] +
                  carbonRemovalTechnologiesProjection.carbonRemovalProjections.directAirCapture[dataIndex] +
                  carbonRemovalTechnologiesProjection.carbonRemovalProjections.bioenergyCarbonCapture[dataIndex];

                return [`Total (Your Scenario): ${mainScenarioTotal.toFixed(1)} Mt CO₂/year`];
              }
              return [];
            }
          }
        }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Year'
        }
      },
      y: {
        type: 'linear',
        position: 'left',
        title: {
          display: true,
          text: 'Carbon Removal Potential (Mt CO₂/year)'
        },
        beginAtZero: true,
        suggestedMax: 20,
        ticks: {
          stepSize: 2
        }
      }
    }
  };

  return (
    <div ref={ref} className="pt-2 bg-white rounded-br-lg rounded-bl-lg shadow flex-grow overflow-hidden flex flex-col mt-0 mb-32">
      <div className="border-b">
        <nav className="flex flex-wrap gap-2 sm:gap-3 lg:gap-4 px-2 sm:px-4 lg:px-6 py-3 justify-center">
          <button
            className={`px-2 sm:px-3 py-2 text-xs sm:text-sm lg:text-base font-medium rounded-md transition-colors whitespace-nowrap ${activeTab === 'impact' ? 'bg-blue-50 text-blue-600 border border-blue-200' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('impact')}
          >
            Overall Impact
          </button>
          <button
            className={`px-2 sm:px-3 py-2 text-xs sm:text-sm lg:text-base font-medium rounded-md transition-colors whitespace-nowrap ${activeTab === 'overview' ? 'bg-blue-50 text-blue-600 border border-blue-200' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('overview')}
          >
            Path to Net Zero
          </button>
          <button
            className={`px-2 sm:px-3 py-2 text-xs sm:text-sm lg:text-base font-medium rounded-md transition-colors whitespace-nowrap ${activeTab === 'power' ? 'bg-blue-50 text-blue-600 border border-blue-200' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('power')}
          >
            <span className="hidden sm:inline">Clean Electricity Supply</span>
            <span className="sm:hidden">Electricity</span>
          </button>
          <button
            className={`px-2 sm:px-3 py-2 text-xs sm:text-sm lg:text-base font-medium rounded-md transition-colors whitespace-nowrap ${activeTab === 'removals' ? 'bg-blue-50 text-blue-600 border border-blue-200' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('removals')}
          >
            <span className="hidden sm:inline">Carbon Dioxide Removals</span>
            <span className="sm:hidden">CO₂ Removals</span>
          </button>
          <button
            className={`px-2 sm:px-3 py-2 text-xs sm:text-sm lg:text-base font-medium rounded-md transition-colors whitespace-nowrap ${activeTab === 'sectors' ? 'bg-blue-50 text-blue-600 border border-blue-200' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('sectors')}
          >
            Sectors
          </button>

        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-2 sm:p-2 lg:p-6 flex-grow">


        {activeTab === 'overview' && (
          <Overview
            scenarioKey='{scenarioKey}'
            sectorImpact={sectorImpact}
            calculateTotalImpact={calculateTotalImpact}
            energyDemandReductionPercentage={energyDemandReductionPercentage}
            energyDemandData={energyDemandData}
            energyDemandOptions={energyDemandOptions}
            comparisonScenario={comparisonScenario}
            setComparisonScenario={setComparisonScenario}
            netZeroBalanceData={netZeroBalanceData}
            netZeroBalanceOptions={netZeroBalanceOptions}
            emissionsProjection={emissionsProjection}
        />
        )}

        {activeTab === 'power' && (
         <Power
            scenarioKey={scenarioKey}
            powerGenerationContext={powerGenerationContext}
            electricityComparisonScenario={electricityComparisonScenario}
            setElectricityComparisonScenario={setElectricityComparisonScenario}
            electricityComparisonData={electricityComparisonData}
            currentContexts={currentContexts}
            selectedContexts={selectedContexts}
            comparisonContexts={comparisonContexts}
            electricityYearsComparisonScenario={electricityYearsComparisonScenario}
            setElectricityYearsComparisonScenario={setElectricityYearsComparisonScenario}
            carbonIntensityData={carbonIntensityData}
            carbonIntensityOptions={carbonIntensityOptions}
            carbonIntensityDetails={carbonIntensityDetails}
            currentScenarioInfo={currentScenarioInfo}
            safePowerGenerationNeeds={safePowerGenerationNeeds}
        />
        )}

        {activeTab === 'removals' && (
          <Removals
                scenarioKey={scenarioKey}
                carbonRemovalTechnologiesData={carbonRemovalTechnologiesData}
                carbonRemovalTechnologiesOptions={carbonRemovalTechnologiesOptions}
                carbonRemovalComparisonScenario={carbonRemovalComparisonScenario}
                setCarbonRemovalComparisonScenario={setCarbonRemovalComparisonScenario}
                carbonRemovalTechnologiesProjection={carbonRemovalTechnologiesProjection}
            />
        )}

        {activeTab === 'sectors' && (
          <Sectors
                sectorImpact={sectorImpact}
                scenarioKey={scenarioKey}
                calculateIndividualSectorEmissionsReduction={calculateIndividualSectorEmissionsReduction}
                calculateSectorEnergyDemandByVector={calculateSectorEnergyDemandByVector}
                // optional: labels={["2020","2025","2030","…"]} if you want to override
            />
        )}

        {activeTab === 'impact' && (
        <Impact
            sectorImpact={sectorImpact}
            calculateTotalImpact={calculateTotalImpact}
            scenarioKey={scenarioKey}                        // from your scenario mapper
            emissionsProjection={emissionsProjection}        // if you have it
            investmentDetails={investmentDetails}            // if you have it
            from={2025}
            to={2050}
        />
        )}
      </div>
    </div>
  );
});

export default ResultsTabs;
